import { useManagePostRequest } from '../../../../../../hooks/useManagePostRequest/useManagePostRequest';
import { responseCode200 } from '../../../../../../consumers/httpRequiestsUtils';
import { useContext } from 'react';
import { AuthContext } from '../../../../../../hooks/context/authContext';
import {
  preguntasCrudAPI,
  opcionesCrudAPI
} from '../../../../../../consumers/backendApisUrls';

/**
 * Parsea el campo Data del response del backend.
 * El backend puede devolver Data como objeto directo o como string JSON anidado.
 * Ej: response.data.data.Data = '{"idPregunta":5}' o = {idPregunta:5}
 */
const parseDataField = (response) => {
  try {
    const raw = response?.data?.data?.Data;
    if (!raw) return null;
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (e) {
    return null;
  }
};

/**
 * Hook personalizado para manejar las operaciones CRUD de preguntas y opciones
 * Centraliza toda la logica de guardado, actualizacion y eliminacion
 */
export const usePreguntasOperations = (evaluacionId) => {
  const [managePostRequest] = useManagePostRequest();
  const { state } = useContext(AuthContext);
  const currentUser = state?.user?.Usuario || state?.user?.email || 'sistema';

  /**
   * Guardar pregunta en la base de datos
   */
  const guardarPreguntaEnBD = async (pregunta, callbacks = {}, evaluacionIdOverride = null) => {
    const { onStart, onSuccess, onError } = callbacks;
    const idEvaluacionToUse = evaluacionIdOverride || evaluacionId;

    if (!idEvaluacionToUse) {
      console.error('Debe guardar la evaluacion primero antes de agregar preguntas a la base de datos');
      return;
    }

    if (!pregunta.pregunta || pregunta.pregunta.trim() === '') {
      console.error('Debe ingresar el texto de la pregunta antes de guardar');
      return;
    }

    if (onStart) {
      onStart();
    }

    // Determinar si es nueva o existente
    const esNueva = typeof pregunta.id === 'string' && pregunta.id.startsWith('nueva_');
    const accion = esNueva ? 'add' : 'edit';

    // Payload segun especificacion del backend
    const payload = esNueva 
      ? {
          accion: 'add',
          idEvaluacion: parseInt(idEvaluacionToUse),
          numero: pregunta.numero,
          textoPregunta: pregunta.pregunta,
          esObligatoria: pregunta.esObligatoria,
          usuario: currentUser
        }
      : {
          accion: 'edit',
          idPregunta: parseInt(pregunta.id),
          textoPregunta: pregunta.pregunta,
          esObligatoria: pregunta.esObligatoria
        };

    console.log(`[${accion.toUpperCase()}] Pregunta en BD:`, payload);

    await managePostRequest(
      preguntasCrudAPI(),
      payload,
      (response) => {
        console.log('[OK] Pregunta guardada en BD:', response);
        
        // Si se creo una nueva pregunta, devolver el ID real
        const parsedPregunta = parseDataField(response);
        if (esNueva && parsedPregunta?.idPregunta) {
          const idReal = parsedPregunta.idPregunta;
          console.log(`[UPDATE] ID temporal -> ID real: ${idReal}`);
          if (onSuccess) {
            onSuccess(idReal);
          }
        } else {
          if (onSuccess) {
            onSuccess();
          }
        }
      },
      false,
      (error) => {
        console.error('[ERROR] Al guardar pregunta en BD:', error);
        if (onError) {
          onError(error);
        }
      }
    );
  };

  /**
   * Guardar opcion en la base de datos
   */
  const guardarOpcionEnBD = async (pregunta, opcion, callbacks = {}) => {
    const { onStart, onSuccess, onError } = callbacks;

    if (!pregunta.id || (typeof pregunta.id === 'string' && pregunta.id.startsWith('nueva_'))) {
      console.error('Debe guardar la pregunta primero antes de guardar sus opciones en la base de datos');
      return;
    }

    if (!opcion.texto || opcion.texto.trim() === '') {
      console.error('Debe ingresar el texto de la opcion antes de guardar');
      return;
    }

    if (onStart) {
      onStart();
    }

    // Determinar si es nueva o existente
    const esNueva = typeof opcion.id === 'string' && opcion.id.startsWith('opcion_');
    const accion = esNueva ? 'add' : 'edit';

    // Obtener el indice de la opcion para el orden
    const ordenOpcion = pregunta.opciones.findIndex(o => o.id === opcion.id) + 1;

    // Payload segun especificacion del backend
    const payload = esNueva
      ? {
          accion: 'add',
          idPregunta: parseInt(pregunta.id),
          ordenOpcion: ordenOpcion,
          textoOpcion: opcion.texto,
          esCorrecta: opcion.esCorrecta,
          puntajeOpcion: parseFloat(opcion.puntaje) || 0
        }
      : {
          accion: 'edit',
          idPregunta: parseInt(pregunta.id),
          idOpcion: parseInt(opcion.id),
          ordenOpcion: ordenOpcion,
          textoOpcion: opcion.texto,
          esCorrecta: opcion.esCorrecta,
          puntajeOpcion: parseFloat(opcion.puntaje) || 0
        };

    console.log(`[${accion.toUpperCase()}] Opcion en BD:`, payload);

    await managePostRequest(
      opcionesCrudAPI(),
      payload,
      (response) => {
        console.log('[OK] Opcion guardada en BD:', response);
        
        // Si se creo una nueva opcion, devolver el ID real
        const parsedOpcion = parseDataField(response);
        const idReal = parsedOpcion?.idOpcion || parsedOpcion?.idOpcionRespuesta;
        if (esNueva && idReal) {
          console.log(`[UPDATE] ID temporal -> ID real opcion: ${idReal}`);
          if (onSuccess) {
            onSuccess(idReal);
          }
        } else {
          if (onSuccess) {
            onSuccess();
          }
        }
      },
      false,
      (error) => {
        console.error('[ERROR] Al guardar opcion en BD:', error);
        if (onError) {
          onError(error);
        }
      }
    );
  };

  /**
   * Eliminar pregunta de la base de datos
   */
  const eliminarPreguntaDeBD = async (pregunta, callbacks = {}) => {
    const { onSuccess, onError } = callbacks;

    // Solo intentar eliminar si tiene un ID numerico (existe en BD)
    if (typeof pregunta.id === 'number' || (typeof pregunta.id === 'string' && !pregunta.id.startsWith('nueva_'))) {
      const payload = {
        accion: 'delete',
        idPregunta: parseInt(pregunta.id)
      };

      console.log('[DELETE] Pregunta de BD:', payload);

      const response = await managePostRequest(
        preguntasCrudAPI(),
        payload,
        (response) => {
          console.log('[OK] Pregunta eliminada de BD:', response);
          if (onSuccess) {
            onSuccess();
          }
        },
        false,
        (error) => {
          console.error('[ERROR] Al eliminar pregunta de BD:', error);
          if (onError) {
            onError(error);
          }
        },
        true
      );

      return responseCode200(response);
    } else {
      console.log('[INFO] Pregunta solo local, no se elimina de BD:', pregunta.id);
      if (onSuccess) {
        onSuccess();
      }
      return true;
    }
  };

  /**
   * Eliminar opcion de la base de datos
   */
  const eliminarOpcionDeBD = async (opcionId, preguntaId, callbacks = {}) => {
    const { onSuccess, onError } = callbacks;

    // Solo intentar eliminar si tiene un ID numerico (existe en BD)
    if (typeof opcionId === 'number' || (typeof opcionId === 'string' && !opcionId.startsWith('opcion_'))) {
      const payload = {
        accion: 'delete',
        idOpcion: parseInt(opcionId),
        idPregunta: preguntaId ? parseInt(preguntaId) : null
      };

      console.log('[DELETE] Opcion de BD:', payload);

      await managePostRequest(
        opcionesCrudAPI(),
        payload,
        (response) => {
          console.log('[OK] Opcion eliminada de BD:', response);
          if (onSuccess) {
            onSuccess();
          }
        },
        false,
        (error) => {
          console.error('[ERROR] Al eliminar opcion de BD:', error);
          if (onError) {
            onError(error);
          }
        }
      );
    } else {
      console.log('[INFO] Opcion solo local, no se elimina de BD:', opcionId);
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  /**
   * Guardar pregunta y todas sus opciones.
   * Versión correctamente secuencial: cada await se completa antes del siguiente.
   * No anida lógica async dentro de callbacks de managePostRequest.
   */
  const guardarTodoEnBD = async (pregunta, callbacks = {}, evaluacionIdOverride = null) => {
    const { onStart, onProgress, onSuccess, onError } = callbacks;
    const idEvaluacionToUse = evaluacionIdOverride || evaluacionId;

    if (onStart) onStart();

    try {
      // ── Paso 1: Guardar pregunta ──────────────────────────────────────────
      if (onProgress) onProgress('Guardando pregunta...');

      const esNuevaPregunta = typeof pregunta.id === 'string' && pregunta.id.startsWith('nueva_');

      if (!idEvaluacionToUse) throw new Error('Se requiere evaluacionId para guardar la pregunta.');
      if (!pregunta.pregunta || pregunta.pregunta.trim() === '') throw new Error('textoPregunta no puede estar vacío.');

      const payloadPregunta = esNuevaPregunta
        ? {
            accion: 'add',
            idEvaluacion: parseInt(idEvaluacionToUse),
            numero: pregunta.numero,
            textoPregunta: pregunta.pregunta,
            esObligatoria: pregunta.esObligatoria,
            usuario: currentUser
          }
        : {
            accion: 'edit',
            idPregunta: parseInt(pregunta.id),
            textoPregunta: pregunta.pregunta,
            esObligatoria: pregunta.esObligatoria
          };

      console.log('[GUARDAR TODO] Pregunta:', payloadPregunta);
      // Llamar sin callback: usamos el response devuelto directamente
      const preguntaResponse = await managePostRequest(preguntasCrudAPI(), payloadPregunta, null, false);

      if (!responseCode200(preguntaResponse)) {
        throw new Error('Error al guardar la pregunta: ' + (preguntaResponse?.data?.message || preguntaResponse?.status));
      }

      const parsedPreguntaData = parseDataField(preguntaResponse);
      const idRealPregunta = esNuevaPregunta
        ? (parsedPreguntaData?.idPregunta ?? null)
        : parseInt(pregunta.id);

      console.log('[GUARDAR TODO] idPregunta real:', idRealPregunta);

      // Si es nueva y no obtuvimos el ID real, no podemos guardar opciones
      if (esNuevaPregunta && !idRealPregunta) {
        console.warn('[GUARDAR TODO] No se obtuvo idPregunta del servidor. Opciones no guardadas.');
        if (onSuccess) onSuccess(null, []);
        return;
      }

      const preguntaActualizada = { ...pregunta, id: idRealPregunta || pregunta.id };

      // ── Paso 2: Guardar opciones secuencialmente ──────────────────────────
      const opcionesActualizadas = [];

      for (const opcion of preguntaActualizada.opciones || []) {
        if (!opcion.texto || opcion.texto.trim() === '') {
          // Opción sin texto, saltar
          opcionesActualizadas.push(opcion);
          continue;
        }

        if (onProgress) onProgress(`Guardando opción ${opcionesActualizadas.length + 1}...`);

        const esNuevaOpcion = typeof opcion.id === 'string' && opcion.id.startsWith('opcion_');
        const ordenOpcion = preguntaActualizada.opciones.findIndex(o => o.id === opcion.id) + 1;

        const payloadOpcion = esNuevaOpcion
          ? {
              accion: 'add',
              idPregunta: parseInt(preguntaActualizada.id),
              ordenOpcion: ordenOpcion,
              textoOpcion: opcion.texto,
              esCorrecta: opcion.esCorrecta,
              puntajeOpcion: parseFloat(opcion.puntaje) || 0
            }
          : {
              accion: 'edit',
              idPregunta: parseInt(preguntaActualizada.id),
              idOpcion: parseInt(opcion.id),
              ordenOpcion: ordenOpcion,
              textoOpcion: opcion.texto,
              esCorrecta: opcion.esCorrecta,
              puntajeOpcion: parseFloat(opcion.puntaje) || 0
            };

        console.log('[GUARDAR TODO] Opción:', payloadOpcion);
        const opcionResponse = await managePostRequest(opcionesCrudAPI(), payloadOpcion, null, false);

        if (!responseCode200(opcionResponse)) {
          console.warn('[GUARDAR TODO] Error al guardar opción, continuando...');
          opcionesActualizadas.push(opcion);
          continue;
        }

        const parsedOpcionData = parseDataField(opcionResponse);
        const idRealOpcion = esNuevaOpcion
          ? (parsedOpcionData?.idOpcion ?? parsedOpcionData?.idOpcionRespuesta ?? null)
          : parseInt(opcion.id);

        opcionesActualizadas.push({ ...opcion, id: idRealOpcion || opcion.id });
      }

      console.log('[GUARDAR TODO] Completado. idPregunta:', idRealPregunta, '| Opciones:', opcionesActualizadas.length);
      if (onSuccess) onSuccess(idRealPregunta, opcionesActualizadas);

    } catch (error) {
      console.error('[ERROR] En guardarTodoEnBD:', error);
      if (onError) onError(error);
    }
  };

  return {
    guardarPreguntaEnBD,
    guardarOpcionEnBD,
    eliminarPreguntaDeBD,
    eliminarOpcionDeBD,
    guardarTodoEnBD
  };
};
